import React, { useCallback, useEffect } from "react";
import cn from "classnames";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalProduct.module.sass";
import Icon from "../Icon";

const ModalPolitiqueConfidentialite = ({ visible, onClose, title, figcaption, gallery, isFirstConnexion = false }) => {
  const escFunction = useCallback(
    (e) => {
      if (e.keyCode === 27 && !isFirstConnexion) {
        onClose();
      }
    },
    [onClose, isFirstConnexion]
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    if (visible) {
      const target = document.querySelector("#modal-product");
      disableBodyScroll(target);
    } else {
      clearAllBodyScrollLocks();
    }
  }, [visible]);

  return createPortal(
    visible && (
      <div id="modal-product" className={styles.modal} onClick={isFirstConnexion ? null : undefined}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose}>
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer}>
          <div className={styles.overview}>
            <div className={cn("title-green", styles.subtitle)}>Politique de Confidentialité</div>
            <div className={styles.content}>
              <section id="responsable-traitement" className={styles.section}>
                <h3 className={styles.sectionTitle}>1. Qui est le responsable de traitement ?</h3>
                <p>
                  Le responsable de traitement est VAISSEAU PAUL, Entrepreneur Individuel, 
                  SIREN : 980 721 039, SIRET : 980 721 039 00017 (ci-après « Nous »).
                </p>
                <p>
                  Contact : contact@edutio.fr
                </p>
              </section>

              <section id="donnees-collectees" className={styles.section}>
                <h3 className={styles.sectionTitle}>2. Quelles données collectons-nous ?</h3>
                <p>
                  Une donnée à caractère personnel est une donnée qui permet d'identifier un individu directement 
                  ou par recoupement avec d'autres données. Nous collectons des données qui relèvent des catégories suivantes :
                </p>
                <ul>
                  <li><strong>Données d'identification</strong> : nom*, prénom*, adresse email* et postale, numéro de téléphone</li>
                  <li><strong>Données professionnelles</strong> : identité de l'établissement de formation*, poste occupé*, secteur d'activité</li>
                  <li><strong>Données pédagogiques</strong> : informations sur les apprenants, parcours de formation, résultats, émargement</li>
                  <li><strong>Données de contact</strong> : toute information transmise dans le cadre de vos demandes</li>
                  <li><strong>Données techniques</strong> : adresse IP, données de navigation, cookies</li>
                </ul>
                <p>
                  Les données obligatoires sont indiquées par un astérisque (*) et sont nécessaires pour vous fournir nos services 
                  de gestion de formation et d'apprentissage.
                </p>
              </section>

              <section id="bases-legales" className={styles.section}>
                <h3 className={styles.sectionTitle}>3. Sur quelles bases légales, pour quelles finalités, et pendant combien de temps conservons-nous vos données personnelles ?</h3>
                
                <div className={styles.tableContainer}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>Finalité</th>
                        <th>Base légale</th>
                        <th>Durée de conservation</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Gestion des comptes utilisateurs</td>
                        <td>Exécution du contrat</td>
                        <td>Durée du contrat + 3 ans</td>
                      </tr>
                      <tr>
                        <td>Gestion des apprenants et formations</td>
                        <td>Obligation légale / Intérêt légitime</td>
                        <td>Durée légale de conservation des dossiers de formation</td>
                      </tr>
                      <tr>
                        <td>Support client et assistance</td>
                        <td>Intérêt légitime</td>
                        <td>3 ans après la dernière interaction</td>
                      </tr>
                      <tr>
                        <td>Amélioration des services</td>
                        <td>Intérêt légitime</td>
                        <td>2 ans</td>
                      </tr>
                      <tr>
                        <td>Communication marketing</td>
                        <td>Consentement</td>
                        <td>Jusqu'au retrait du consentement</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="destinataires" className={styles.section}>
                <h3 className={styles.sectionTitle}>4. Qui sont les destinataires de vos données ?</h3>
                <p>Auront accès à vos données à caractère personnel :</p>
                <ul>
                  <li><strong>Le personnel d'Edutio</strong> : dans le cadre de leurs missions</li>
                  <li><strong>Nos sous-traitants</strong> : hébergeurs, services de maintenance, outils d'analyse</li>
                  <li><strong>Partenaires pédagogiques</strong> : OPCO, organismes certificateurs (avec votre accord)</li>
                  <li><strong>Organismes publics</strong> : exclusivement pour répondre à nos obligations légales</li>
                </ul>
                <p>
                  Tous nos sous-traitants sont soumis à des obligations contractuelles strictes de confidentialité 
                  et de sécurité conformes au RGPD.
                </p>
              </section>

              <section id="transferts-ue" className={styles.section}>
                <h3 className={styles.sectionTitle}>5. Vos données sont-elles susceptibles d'être transférées hors de l'Union Européenne ?</h3>
                <p>
                  Vos données sont principalement conservées et stockées sur des serveurs situés dans l'Union Européenne. 
                  Cependant, dans le cadre de certains outils que nous utilisons, vos données peuvent faire l'objet 
                  de transferts hors de l'Union Européenne.
                </p>
                <p>Le transfert de vos données dans ce cadre est sécurisé au moyen des outils suivants :</p>
                <ul>
                  <li>Transferts vers des pays ayant fait l'objet d'une décision d'adéquation de la Commission européenne</li>
                  <li>Clauses contractuelles types approuvées par la Commission Européenne</li>
                  <li>Mécanismes de certification approuvés conformément au RGPD</li>
                </ul>
              </section>

              <section id="vos-droits" className={styles.section}>
                <h3 className={styles.sectionTitle}>6. Quels sont vos droits sur vos données ?</h3>
                <p>Vous disposez des droits suivants s'agissant de vos données personnelles :</p>
                
                <div className={styles.rightsList}>
                  <div className={styles.rightItem}>
                    <h4>Droit à l'information</h4>
                    <p>C'est la raison pour laquelle nous avons rédigé cette politique (articles 13 et 14 du RGPD).</p>
                  </div>
                  
                  <div className={styles.rightItem}>
                    <h4>Droit d'accès</h4>
                    <p>Vous avez le droit d'accéder à tout moment à l'ensemble de vos données personnelles (article 15 du RGPD).</p>
                  </div>
                  
                  <div className={styles.rightItem}>
                    <h4>Droit de rectification</h4>
                    <p>Vous avez le droit de rectifier vos données personnelles inexactes, incomplètes ou obsolètes (article 16 du RGPD).</p>
                  </div>
                  
                  <div className={styles.rightItem}>
                    <h4>Droit à la limitation</h4>
                    <p>Vous avez le droit d'obtenir la limitation du traitement dans certains cas définis (article 18 du RGPD).</p>
                  </div>
                  
                  <div className={styles.rightItem}>
                    <h4>Droit à l'effacement</h4>
                    <p>Vous avez le droit d'exiger que vos données soient effacées dans les conditions de l'article 17 du RGPD.</p>
                  </div>
                  
                  <div className={styles.rightItem}>
                    <h4>Droit de portabilité</h4>
                    <p>Vous avez le droit de recevoir vos données dans un format standard et de les transférer (article 20 du RGPD).</p>
                  </div>
                  
                  <div className={styles.rightItem}>
                    <h4>Droit d'opposition</h4>
                    <p>Vous avez le droit de vous opposer au traitement de vos données personnelles (article 21 du RGPD).</p>
                  </div>
                  
                  <div className={styles.rightItem}>
                    <h4>Droit de réclamation</h4>
                    <p>Vous avez le droit d'introduire une réclamation auprès de la CNIL si vous considérez que le traitement constitue une violation (article 77 du RGPD).</p>
                  </div>
                </div>
                
                <p>
                  Vous pouvez exercer ces droits en nous écrivant aux coordonnées ci-dessous. 
                  Nous pourrons vous demander de nous fournir des informations complémentaires pour justifier votre identité.
                </p>
              </section>

              <section id="point-contact" className={styles.section}>
                <h3 className={styles.sectionTitle}>7. Point de contact en matière de données personnelles</h3>
                <div className={styles.contactInfo}>
                  <p><strong>Email de contact :</strong> contact@edutio.fr</p>
                  <p><strong>Adresse de contact :</strong> 165 Avenue de Bretagne, 59000 Lille, France</p>
                  <p><strong>Téléphone :</strong> 0627361850</p>
                </div>
              </section>

              <section id="modifications" className={styles.section}>
                <h3 className={styles.sectionTitle}>8. Modifications</h3>
                <p>
                  Nous pouvons modifier à tout moment la présente politique, afin notamment de nous conformer 
                  à toutes évolutions réglementaires, jurisprudentielles, éditoriales ou techniques.
                </p>
                <p>
                  Ces modifications s'appliqueront à la date d'entrée en vigueur de la version modifiée. 
                  Vous êtes donc invité à consulter régulièrement la dernière version de cette politique.
                </p>
                <p>
                  Néanmoins, nous vous tiendrons informé(e) de toute modification significative de la présente 
                  politique de confidentialité par email ou notification sur la plateforme.
                </p>
                <p>
                  <strong>Entrée en vigueur :</strong> {new Date().toLocaleDateString('fr-FR')}
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    ),
    document.body
  );
};

export default ModalPolitiqueConfidentialite;
